import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { CheckboxWithLabel, TextField } from 'formik-material-ui';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Grid from '@material-ui/core/Grid';
import {
  Button,
  TextField as MuiTextField,
  CircularProgress,
  NativeSelect,
  makeStyles,
  InputLabel,
} from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input';
import { Autocomplete } from '@material-ui/lab';
import * as yup from 'yup';
import has from 'lodash/has';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import 'react-phone-number-input/style.css';

import { trackAhoyEvent, updateAhoyToken } from 'utils/ahoy';
import { postRequest } from 'utils/api';
import {
  getTokenFromResponse,
  identifyFullStoryUser,
  setCrispUser,
  sports,
  USStates,
} from 'utils/helpers';
import { signIn } from 'features/authSlice';
import { setFooter, setRoleFilter } from 'features/utilsSlice';
import {
  getReferralCode,
  saveAccessToken,
  setLastSignInRole,
  setInventorySetup,
  getUuid,
  setRedirectTo,
  getRedirectTo,
  deleteUuid,
  setCurrentUserLS,
} from 'utils/localStorage';
import Email from 'public/icons/form/email.svg';
import User from 'public/icons/form/user.svg';
import ChevronDownDarkIcon from 'public/icons/chevron-down-dark.svg';
import AnimateListItem from 'components/animations/AnimateListItem';
import Switch from 'components/Switch';
import AnimateList from 'components/animations/AnimateList';
import SchoolsAutocomplete from 'components/SchoolsAutocomplete';
import PasswordConfirmation from 'components/PasswordConfirmation';
import styles from 'styles/components/auth/AuthForm.module.scss';

const defaultRequired = 'is required.';
const width = { width: '100%' };
const marginBottom = { marginBottom: 20 };
const relative = { position: 'relative' };

const useStyles = makeStyles({
  textFieldRoot: {
    height: 'unset',
  },
  popupIndicator: {
    marginTop: 'unset !important',
  },
  deleteIcon: {
    width: '0.8em',
    height: '0.8em',
  },
});

const schema = yup.object({
  contact_email: yup
    .string()
    .email('is not a valid email')
    .required('is required'),
  phone: yup.number().required(defaultRequired),
  notification_type: yup.string().required(defaultRequired),
  profiles: yup.object({
    display_name: yup.string().required(defaultRequired),
    username: yup
      .string()
      .min(4, 'Must contain at least 4 characters')
      .matches(/^[a-zA-Z0-9_.-]*$/, 'is invalid (no spaces or special characters allowed).')
      .required(defaultRequired),
    school_location: yup.string().required(defaultRequired),
    school_type: yup.string(),
    athlete_level: yup.string().required(defaultRequired),
    school: yup.string().when(['school_location', 'athlete_level'], (location, athleteLevel) => {
      if (location === 'US' && athleteLevel === 'student_athlete') {
        return yup.object().shape({
          id: yup.number().required(defaultRequired),
          name: yup.string().required(defaultRequired),
        });
      }

      return yup.mixed();
    }),
    school_name: yup.string().when('school_location', location => {
      if (location === 'International') {
        return yup.string().required(defaultRequired);
      }

      return yup.string();
    }),
    state: yup.string().when(['school_location', 'athlete_level'], (location, athleteLevel) => {
      if (location === 'US' && athleteLevel === 'student_athlete') {
        return yup.object().shape({
          code: yup.string().required(defaultRequired),
          name: yup.string().required(defaultRequired),
        });
      }

      return yup.mixed();
    }),
    team_name: yup.string().when('athlete_level', athleteLevel => {
      if (athleteLevel === 'professional_athlete') {
        return yup.string().required(defaultRequired);
      }

      return yup.string();
    }),
    sports: yup
      .array()
      .required(defaultRequired)
      .min(1)
      .max(5),
  }),

  password: yup
    .string()
    .required(defaultRequired)
    .min(6, 'Must contain at least 6 characters'),
  password_confirmation: yup
    .string()
    .required(defaultRequired)
    .oneOf([yup.ref('password')], "Passwords don't match"),

  notifications: yup
    .bool()
    .oneOf(
      [true],
      'Notifications are required for users on our site: notifications for new gigs, when to post, when to take down posts, when you’ve made money, when you can cash out, new gig requests, messages from buyers and sellers etc. This is required functionality for our site to work.'
    ),
});

const initialValues = {
  contact_email: '',
  phone: undefined,
  notification_type: 'sms',
  profiles: {
    display_name: '',
    username: '',
    school_location: 'US',
    athlete_level: 'student_athlete',
    school_type: 'college',
    state: '',
    school_name: '',
    team_name: '',
    sports: [],
    athlete: true,
  },
  password: '',
  password_confirmation: '',
  notifications: false,
};

const AthleteForm = ({ sendToken = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const router = useRouter();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const handleSubmit = ({ profiles, ...values }, actions) => {
    setIsLoading(true);
    const refCode = getReferralCode();
    const uuid = getUuid();
    const redirectTo = getRedirectTo();

    postRequest({
      endpoint: 'sign_up',
      data: {
        user: {
          profiles_attributes: [{ ...profiles, school_id: profiles?.school?.id || null }],
          ...values,
          ref_code: refCode,
          role: 'influencer',
          primary_interest: 'sell',
          notification_type:
            parsePhoneNumber(values.phone || '')?.country === 'US'
              ? values.notification_type
              : 'whatsapp',
        },
        uuid,
      },
      successMessage: !uuid || (uuid && redirectTo) ? 'Successfully signed up.' : '',

      version: null,
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
        sendToken,
      },
      ...actions,
    })
      .then(response => {
        const token = getTokenFromResponse(response);

        if (token) saveAccessToken(token);

        dispatch(setRoleFilter(uuid ? 'advertiser' : 'influencer'));

        if (uuid) deleteUuid();

        dispatch(signIn(response.data));
        setCurrentUserLS(response.data);
        setCrispUser(response.data);
        identifyFullStoryUser(response.data);
        setLastSignInRole('influencer');
        setIsLoading(false);

        if (token) updateAhoyToken(token);
        trackAhoyEvent('Sign Up', { role: response.data.role, id: response.data.id });

        if (uuid && redirectTo) {
          router.push(redirectTo);
          setRedirectTo(null);
          return;
        }

        setInventorySetup(true);
        router.push('/access-confirmation');
      })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    if (isMobile) {
      dispatch(setFooter(false));
    }

    return () => dispatch(setFooter(true));
  }, [dispatch, isMobile]);

  return (
    <AnimateList>
      <Grid container className={styles.container} direction="column" alignItems="center">
        <h1>Athlete sign up</h1>

        <Grid style={width} item xs={11} sm={6} md={4} lg={3} xl={2}>
          <Formik
            validationSchema={schema}
            initialValues={{
              ...initialValues,
              profiles: { ...initialValues.profiles },
            }}
            onSubmit={handleSubmit}
            noValidate
            enableReinitialize
          >
            {({
              isSubmiting,
              values,
              setFieldValue,
              handleBlur,
              errors,
              touched,
              setFieldTouched,
            }) => (
              <Form className={styles.form}>
                <div style={marginBottom}>
                  <Switch
                    firstOption={{ label: 'Student Athlete', value: 'student_athlete' }}
                    secondOption={{
                      label: 'Professional Athlete',
                      value: 'professional_athlete',
                    }}
                    setValue={value => {
                      if (value === 'student_athlete') {
                        setFieldValue('profiles.athlete_level', value);
                        setFieldValue('profiles.team_name', '');
                        setFieldValue('profiles.state', '');
                        setFieldValue('profiles.school_location', 'US');
                      } else {
                        setFieldValue('profiles.athlete_level', value);
                        setFieldValue('profiles.state', '');
                        setFieldValue('profiles.school', {});
                        setFieldValue('profiles.school_name', '');

                        if (values.profiles.sports.length > 1) {
                          setFieldValue('profiles.sports', values.profiles.sports.slice(0, 1));
                        }
                      }
                    }}
                    value={values.profiles.athlete_level}
                  />
                </div>

                {values.profiles.athlete_level === 'student_athlete' && (
                  <>
                    <InputLabel>Select location of school</InputLabel>

                    <div style={marginBottom}>
                      <NativeSelect
                        fullWidth
                        name="profiles.school_location"
                        className={styles.select}
                        value={values.profiles.school_location}
                        onChange={e => {
                          if (e.target.value === 'International') {
                            setFieldValue('profiles.state', '');
                            setFieldValue('profiles.school', {});
                          } else {
                            setFieldValue('profiles.school_name', '');
                          }

                          setFieldValue('profiles.school_location', e.target.value);
                        }}
                        IconComponent={ChevronDownDarkIcon}
                      >
                        <option value="US">United States</option>
                        <option value="International">International</option>
                      </NativeSelect>
                    </div>

                    <div style={marginBottom}>
                      <NativeSelect
                        fullWidth
                        name="profiles.school_type"
                        className={styles.select}
                        value={values.profiles.school_type}
                        onChange={e => setFieldValue('profiles.school_type', e.target.value)}
                        IconComponent={ChevronDownDarkIcon}
                      >
                        <option value="college">College / University</option>
                        <option value="school">Middle school / High school</option>
                      </NativeSelect>
                    </div>

                    {values.profiles.school_location === 'US' && (
                      <>
                        <Autocomplete
                          id="state"
                          classes={{
                            popupIndicator: classes.popupIndicator,
                          }}
                          ChipProps={{
                            classes: {
                              deleteIcon: classes.deleteIcon,
                            },
                          }}
                          value={values.profiles.state}
                          onChange={(_, value, __) => {
                            let newValue = value || {};

                            setFieldValue('profiles.state', newValue).then(() => {
                              setFieldTouched('profiles.state');
                            });

                            setFieldValue('profiles.school', {}).then(() => {
                              setFieldTouched('profile.school');
                            });
                          }}
                          options={USStates}
                          getOptionLabel={option => option.name || ''}
                          getOptionSelected={(option, value) => option.code === value.code}
                          blurOnSelect
                          renderInput={params => (
                            <MuiTextField
                              {...params}
                              variant="standard"
                              classes={{ root: classes.textFieldRoot }}
                              InputProps={{
                                ...params.InputProps,
                                name: 'profiles.state',
                                placeholder: 'Select your school state',
                                classes: {
                                  adornedStart: classes.adornedStart,
                                  input: classes.input,
                                },
                                error: Boolean(touched?.profiles?.state && errors?.profiles?.state),
                                onBlur: handleBlur,
                              }}
                            />
                          )}
                        />

                        <ErrorMessage component="span" name="profiles.state">
                          {_msg => <span className="error-msg ml-0">is required.</span>}
                        </ErrorMessage>

                        {values.profiles.state && (
                          <>
                            <SchoolsAutocomplete
                              name="profiles.school"
                              handleBlur={handleBlur}
                              state={values.profiles.state?.code}
                              schoolType={values.profiles.school_type}
                              handleSelect={option => {
                                setFieldValue('profiles.school', {
                                  id: option.id,
                                  name: option.name,
                                }).then(() => {
                                  setFieldTouched('profiles.school', true);
                                });

                                setFieldValue('profiles.school_name', '').then(() => {
                                  setFieldTouched('profiles.school_name', true);
                                });
                              }}
                              school={values.profiles.school}
                              error={Boolean(touched?.profiles?.school && errors?.profiles?.school)}
                            />

                            <ErrorMessage component="span" name="profiles.school">
                              {_msg => (
                                <span className="error-msg ml-0" style={{ marginTop: -26 }}>
                                  is required.
                                </span>
                              )}
                            </ErrorMessage>
                          </>
                        )}
                      </>
                    )}

                    {values.profiles.school_location !== 'US' && (
                      <Field
                        component={TextField}
                        name="profiles.school_name"
                        type="text"
                        placeholder="Type name of school"
                        variant="standard"
                        color="primary"
                      />
                    )}
                  </>
                )}

                {values.profiles.athlete_level === 'professional_athlete' && (
                  <Field
                    component={TextField}
                    name="profiles.team_name"
                    type="text"
                    placeholder="Team Name"
                    variant="standard"
                    color="primary"
                  />
                )}

                <Autocomplete
                  multiple={values.profiles.athlete_level === 'student_athlete'}
                  id="sports"
                  classes={{
                    popupIndicator: classes.popupIndicator,
                  }}
                  ChipProps={{
                    classes: {
                      deleteIcon: classes.deleteIcon,
                    },
                  }}
                  value={
                    values.profiles.athlete_level === 'student_athlete'
                      ? values.profiles.sports
                      : values.profiles.sports[0] || null
                  }
                  onChange={(_, value, __) => {
                    if (values.profiles.athlete_level === 'student_athlete') {
                      if (value.length > 5) return;

                      setFieldValue('profiles.sports', value);
                    } else {
                      const newValue = value ? [value] : [];
                      setFieldValue('profiles.sports', newValue);
                    }
                  }}
                  options={sports}
                  getOptionLabel={option => option || ''}
                  renderInput={params => (
                    <MuiTextField
                      {...params}
                      variant="standard"
                      name="profiles.sports"
                      classes={{ root: classes.textFieldRoot }}
                      InputProps={{
                        ...params.InputProps,
                        placeholder:
                          values.profiles.athlete_level === 'student_athlete'
                            ? values.profiles.sports.length === 0
                              ? 'Sports (type the name, select up to 5)'
                              : ''
                            : 'Primary Sport',
                        classes: {
                          adornedStart: classes.adornedStart,
                          input: classes.input,
                        },
                        error: Boolean(touched?.profiles?.sports && errors?.profiles?.sports),
                        onBlur: handleBlur,
                      }}
                    />
                  )}
                />

                <ErrorMessage component="span" name="profiles.sports">
                  {_msg => <span className="error-msg ml-0">is required.</span>}
                </ErrorMessage>

                <h3 style={{ marginTop: 10 }}>Account Info</h3>

                <AnimateListItem style={relative}>
                  <span style={{ marginLeft: 14, opacity: 0.6, fontSize: 12 }}>
                    This will be the number you get notifications
                  </span>

                  <PhoneInput
                    name="phone"
                    placeholder="Phone Number"
                    onChange={phone => setFieldValue('phone', phone)}
                    defaultCountry={values.profiles.school_location === 'US' ? 'US' : null}
                    onBlur={handleBlur}
                    defaultChecked
                    className={clsx(styles.phoneInput, {
                      [styles.phoneInputError]: has(errors, 'phone', false) && touched.phone,
                    })}
                  />

                  <ErrorMessage
                    name="phone"
                    component="span"
                    style={{ top: 60 }}
                    className={styles.error}
                  />
                </AnimateListItem>

                <AnimateListItem>
                  <Field
                    component={TextField}
                    name="profiles.display_name"
                    type="text"
                    value={values.profiles.display_name}
                    placeholder="Display Name (Name of your page)"
                    variant="outlined"
                    color="primary"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <User width={14} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </AnimateListItem>

                <AnimateListItem>
                  <div style={{ position: 'relative' }}>
                    <Field
                      component={TextField}
                      name="profiles.username"
                      type="text"
                      value={values.profiles.username}
                      placeholder="Username"
                      variant="outlined"
                      color="primary"
                      className={errors['profiles.username'] ? styles.inputError : ''}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <User width={14} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {errors['profiles.username'] && (
                      <span className={styles.error}>{errors['profiles.username']}</span>
                    )}
                  </div>
                </AnimateListItem>

                <AnimateListItem>
                  <div style={{ marginTop: -10 }}>
                    <span style={{ marginLeft: 14, opacity: 0.6, fontSize: 12 }}>
                      Email to receive some notifications and receipts
                    </span>

                    <Field
                      component={TextField}
                      name="contact_email"
                      type="email"
                      placeholder="Email Address"
                      variant="outlined"
                      color="primary"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email width={14} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </AnimateListItem>

                <PasswordConfirmation />

                <div
                  style={{ position: 'relative' }}
                  className={errors.notifications && 'checkbox-nested-error'}
                >
                  <Field
                    component={CheckboxWithLabel}
                    type="checkbox"
                    name="notifications"
                    Label={{
                      label: 'I agree to receive notifications to this phone',
                    }}
                    color="primary"
                  />

                  <ErrorMessage
                    name="notifications"
                    component="span"
                    className={styles.error}
                    style={{ top: -5, position: 'relative' }}
                  />
                </div>

                {parsePhoneNumber(values.phone || '')?.country === 'US' ? (
                  <AnimateListItem>
                    <div style={{ marginTop: 20, marginBottom: 20 }}>
                      <InputLabel>Which way of communication do you prefer?</InputLabel>

                      <Switch
                        firstOption={{ label: 'SMS', value: 'sms' }}
                        secondOption={{ label: 'WhatsApp', value: 'whatsapp' }}
                        setValue={value => setFieldValue('notification_type', value)}
                        value={values.notification_type}
                      />
                    </div>
                  </AnimateListItem>
                ) : (
                  <div style={{ marginTop: 20, marginBottom: 20 }}>
                    We can only send notifications to WhatsApp in your country. Make sure to
                    download the app if you don't have it yet.
                  </div>
                )}

                <AnimateListItem>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={isSubmiting || isLoading || !isEmpty(errors)}
                    type="submit"
                    fullWidth
                  >
                    {isLoading ? <CircularProgress color="primary" /> : 'Register'}
                  </Button>
                </AnimateListItem>
              </Form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </AnimateList>
  );
};

export default AthleteForm;
